/*
 * PERMISSION INDICATOR COMPONENT
 * 
 * Standardized visual indicators for permission states across the application.
 * Provides consistent icons, colors, and tooltips for edit/delete permissions.
 * 
 * Features:
 * - View-only indicator (eye icon) when user cannot edit
 * - Permission description tooltips 
 * - Consistent styling with theme classes
 * - Accessible ARIA labels and roles
 * 
 * Usage:
 * - Use in cards, detail views, and forms to show permission status
 * - Integrates with usePermissions hooks for state management
 */

import { FC } from 'react';

interface PermissionIndicatorProps {
  /** Whether the user can edit this entity */
  canEdit: boolean;
  /** Optional permission description for tooltip */
  description?: string;
  /** Size variant for different contexts */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
}

const PermissionIndicator: FC<PermissionIndicatorProps> = ({
  canEdit,
  description = '',
  size = 'sm',
  className = ''
}) => {
  // Don't show indicator if user has edit permission
  if (canEdit) {
    return null;
  }

  // Size-based styling
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <div 
      className={`flex items-center ${className}`}
      title={description || 'View-only access'}
      role="img"
      aria-label={description || 'View-only access'}
    >
      <svg 
        className={`${sizeClasses[size]} text-yellow-500 flex-shrink-0`} 
        fill="currentColor" 
        viewBox="0 0 20 20"
      >
        {/* Eye icon for view-only access */}
        <path fillRule="evenodd" d="M10 12a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
      </svg>
    </div>
  );
};

export default PermissionIndicator;